import { useEffect, useMemo, useState } from 'react'
import { useRequest } from 'ahooks'
import { getFlowExecuteLog } from '@apis/flow'
import { FlowStatus } from '@apis/flow/type'
import { FlowLogger } from './flowLogger'
import type { FlowOutput } from './types'
import { transformDataToValidLogs, addChildrenToFlowOutput } from './utils'

export interface BusinessFlowLoggerProps {
  task?: Record<string, any>
  status?: 'success' | 'error'
  title?: string
}

export function BusinessFlowLogger({
  task = {},
  title = '',
  status,
}: BusinessFlowLoggerProps) {
  const { runAsync: getFlowExecuteLogApi, data } = useRequest(
    getFlowExecuteLog,
    {
      manual: true,
      ready: Boolean(task?.data?.content?.task_id),
    },
  )

  const [logs, setLogs] = useState<FlowOutput[]>([])

  useEffect(() => {
    if (task) {
      const flowId = task.data?.content?.input?.flow_id

      const taskId = task?.data?.content?.task_id
      if (flowId) {
        getFlowExecuteLogApi({ flowId, taskId })
      }
    }
  }, [task])

  useEffect(() => {
    const taskId = task?.data?.content?.task_id

    const log =
      taskId && data?.run_result?.actionOutputs
        ? transformDataToValidLogs(data?.run_result?.actionOutputs || {})
        : [
            {
              hideOutputLabel: true,
              name: title,
              status:
                status === 'success' ? FlowStatus.SUCCEEDED : FlowStatus.FAIL,
              duration: 2000,
              showTime: false,
              showRun: false,
              input: task?.data?.content?.input || '',
              output: task?.data?.content?.output || '',
              type: task?.data?.tool_type,
              errorMessage: task?.data?.message,
            },
          ]

    setLogs(log)
  }, [data, task])

  const activeKeys = useMemo(() => {
    const taskId = task?.data?.content?.task_id

    const failedLog = logs.find(log => log.status === FlowStatus.FAIL)
    return taskId ? ['start', failedLog ? failedLog?.name : 'output'] : [title]
  }, [logs, task])

  const onChange = (item: FlowOutput, open: boolean) => {
    const key = item.value || ''
    console.log('subFlowLogs', item, key, open)

    if (item.type === 'SUB_FLOW' && open) {
      const flowId = item?.attrs?.flow_id
      const taskId = item?.attrs?.task_id

      getFlowExecuteLog({ flowId, taskId }).then(res => {
        const changedLogs = addChildrenToFlowOutput(
          logs,
          key,
          transformDataToValidLogs(res.run_result.actionOutputs, {
            isSingleStepRun: (res.run_type as string) === 'STEP_RUN',
            taskId: res.task_id,
          }),
        )

        setLogs(changedLogs)
      })
    }
  }

  return (
    <FlowLogger
      defaultActiveKey={activeKeys}
      outputs={logs}
      onChange={onChange}
    />
  )
}
